/**
 * NearbyContext — shared "Near me" filter state.
 *
 * Stores:
 *   enabled   — whether the filter is active
 *   radiusMi  — current radius IN MILES (default 25). UK / US users
 *               think in miles for travel distance, so we expose miles
 *               in the UI and convert to km at compare-time for the
 *               haversine maths (which returns km).
 *   coords    — { lat, lng } once we have a position
 *   status    — 'idle' | 'prompting' | 'granted' | 'denied' | 'unavailable'
 *
 * On first /events visit we auto-prompt for geolocation. The user's
 * choice (granted/denied + radius preference) is persisted in
 * localStorage so we don't re-prompt every load.
 */
import { createContext, useCallback, useContext, useEffect, useState } from 'react';

const LS = {
  enabled:   'rollm8s_nearby_enabled',     // '1' / '0'
  radius:    'rollm8s_nearby_radius_mi',   // number — MILES
  status:    'rollm8s_nearby_status',      // 'granted' | 'denied'
  coords:    'rollm8s_nearby_coords',      // JSON {lat,lng,ts}
  prompted:  'rollm8s_nearby_prompted',    // '1' once we've prompted
};

const DEFAULT_RADIUS_MI = 25;
const MI_TO_KM = 1.609344;
const COORDS_TTL_MS = 1000 * 60 * 60 * 12;     // 12h cache

const NearbyContext = createContext(null);

function haversineKm(a, b) {
  if (!a || !b) return Infinity;
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

function readCachedCoords() {
  try {
    const raw = localStorage.getItem(LS.coords);
    if (!raw) return null;
    const c = JSON.parse(raw);
    if (!c?.lat || !c?.lng || !c?.ts) return null;
    if (Date.now() - c.ts > COORDS_TTL_MS) return null;
    return { lat: c.lat, lng: c.lng };
  } catch { return null; }
}

export function NearbyProvider({ children }) {
  const [enabled, setEnabled] = useState(
    () => localStorage.getItem(LS.enabled) === '1'
  );
  const [radiusMi, setRadiusMi] = useState(() => {
    const n = parseInt(localStorage.getItem(LS.radius), 10);
    return Number.isFinite(n) && n > 0 ? n : DEFAULT_RADIUS_MI;
  });
  const [coords, setCoords] = useState(() => readCachedCoords());
  const [status, setStatus] = useState(
    () => localStorage.getItem(LS.status) || 'idle'
  );

  // ── persistence ─────────────────────────────────────────────────────────
  useEffect(() => { localStorage.setItem(LS.enabled, enabled ? '1' : '0'); }, [enabled]);
  useEffect(() => { localStorage.setItem(LS.radius, String(radiusMi)); }, [radiusMi]);
  useEffect(() => {
    if (status !== 'idle') localStorage.setItem(LS.status, status);
  }, [status]);
  useEffect(() => {
    if (coords) {
      localStorage.setItem(LS.coords, JSON.stringify({ ...coords, ts: Date.now() }));
    }
  }, [coords]);

  const requestPosition = useCallback(() => {
    return new Promise((resolve) => {
      if (!('geolocation' in navigator)) {
        setStatus('unavailable');
        resolve(null);
        return;
      }
      setStatus('prompting');
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const next = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          setCoords(next);
          setStatus('granted');
          resolve(next);
        },
        () => {
          setStatus('denied');
          resolve(null);
        },
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 1000 * 60 * 10 }
      );
    });
  }, []);

  // ── public actions ──────────────────────────────────────────────────────
  const enable = useCallback(async () => {
    let pos = coords;
    if (!pos) pos = await requestPosition();
    if (pos) {
      setEnabled(true);
      localStorage.setItem(LS.prompted, '1');
      return true;
    }
    localStorage.setItem(LS.prompted, '1');
    return false;
  }, [coords, requestPosition]);

  const disable = useCallback(() => setEnabled(false), []);

  const toggle = useCallback(() => (enabled ? disable() : enable()), [enabled, disable, enable]);

  const ensurePromptOnce = useCallback(async () => {
    if (localStorage.getItem(LS.prompted) === '1') return;
    if (status === 'denied') return;
    await enable();
  }, [status, enable]);

  const isWithin = useCallback(
    (point) => {
      if (!enabled) return true;          // chip off → don't filter
      if (!coords) return true;           // we have no anchor → can't filter, keep all
      // Strict: if the event itself has no coordinates we cannot verify
      // distance, so when Near me IS on we hide it rather than letting
      // it leak in from 350 miles away.
      if (!point || typeof point.lat !== 'number' || typeof point.lng !== 'number') {
        return false;
      }
      const distKm = haversineKm(coords, point);
      return distKm <= radiusMi * MI_TO_KM;
    },
    [enabled, coords, radiusMi]
  );

  const value = {
    enabled, radiusMi, coords, status,
    setRadiusMi, enable, disable, toggle,
    ensurePromptOnce, isWithin,
  };

  return <NearbyContext.Provider value={value}>{children}</NearbyContext.Provider>;
}

export default function useNearby() {
  const ctx = useContext(NearbyContext);
  if (!ctx) {
    return {
      enabled: false, radiusMi: DEFAULT_RADIUS_MI, coords: null, status: 'idle',
      setRadiusMi: () => {}, enable: async () => false, disable: () => {},
      toggle: () => {}, ensurePromptOnce: async () => {},
      isWithin: () => true,
    };
  }
  return ctx;
}

export { DEFAULT_RADIUS_MI };
